/**
 * Research RPC definitions
 *
 * Authenticated endpoints for research execution with streaming.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { RedisStreamError } from "@fm/research/services/RedisStreamError";
import { Schema } from "effect";
import { AuthMiddleware } from "../auth.tags.ts";
import { DatabaseError, Unauthorized } from "../errors.ts";

// ============================================================================
// Event Schemas
// ============================================================================

/**
 * Section schema for outline generated event
 * @since 1.0.0
 */
const OutlineSectionSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  description: Schema.String,
});

/**
 * Research event schema - all possible events during research execution
 * @since 1.0.0
 */
export const ResearchEventSchema = Schema.Union(
  // Phase 1: Query expansion
  Schema.Struct({
    type: Schema.Literal("query.expanding"),
    executionId: Schema.String,
    originalQuery: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("query.expanded"),
    executionId: Schema.String,
    expandedQuery: Schema.String,
    subQuestions: Schema.Array(Schema.String),
  }),
  // Phase 2: Outline generation
  Schema.Struct({
    type: Schema.Literal("outline.generating"),
    executionId: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("outline.generated"),
    executionId: Schema.String,
    sections: Schema.Array(OutlineSectionSchema),
  }),
  // Phase 3: Section execution
  Schema.Struct({
    type: Schema.Literal("section.started"),
    executionId: Schema.String,
    sectionId: Schema.String,
    title: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("section.searching"),
    executionId: Schema.String,
    sectionId: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("section.sources.found"),
    executionId: Schema.String,
    sectionId: Schema.String,
    sourceCount: Schema.Number,
  }),
  Schema.Struct({
    type: Schema.Literal("section.writing"),
    executionId: Schema.String,
    sectionId: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("section.completed"),
    executionId: Schema.String,
    sectionId: Schema.String,
    content: Schema.String,
    citationCount: Schema.Number,
  }),
  Schema.Struct({
    type: Schema.Literal("section.failed"),
    executionId: Schema.String,
    sectionId: Schema.String,
    error: Schema.String,
  }),
  // Phase 4: Completion
  Schema.Struct({
    type: Schema.Literal("execution.completed"),
    executionId: Schema.String,
    totalSections: Schema.Number,
    completedSections: Schema.Number,
    failedSections: Schema.Number,
  }),
  Schema.Struct({
    type: Schema.Literal("execution.failed"),
    executionId: Schema.String,
    error: Schema.String,
  }),
  // Reconnection status
  Schema.Struct({
    type: Schema.Literal("execution.expired"),
    executionId: Schema.String,
    message: Schema.String,
  }),
);

/**
 * Type for research events
 * @since 1.0.0
 */
export type ResearchEvent = Schema.Schema.Type<typeof ResearchEventSchema>;

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Request schema for executing research
 *
 * Supports both new executions and reconnection to existing ones:
 * - New execution: Only provide `query`
 * - Reconnection: Provide `executionId` and optionally `lastEventId`
 *
 * @since 1.0.0
 */
const ExecuteResearchRequest = Schema.Struct({
  query: Schema.String.pipe(
    Schema.minLength(10),
    Schema.maxLength(500),
  ),
  /** Optional execution ID for reconnecting to an existing execution */
  executionId: Schema.optional(Schema.String),
  /** Optional last event ID to resume from (Redis stream message ID) */
  lastEventId: Schema.optional(Schema.String),
});

/**
 * Request schema for getting research status
 * @since 1.0.0
 */
const ResearchStatusRequest = Schema.Struct({
  executionId: Schema.String,
});

/**
 * Response schema for research status
 * @since 1.0.0
 */
const ResearchStatusResponse = Schema.Struct({
  id: Schema.String,
  status: Schema.Literal("pending", "running", "completed", "failed"),
  progress: Schema.Number,
  sectionsCompleted: Schema.Number,
  totalSections: Schema.Number,
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});

/**
 * Response schema for listing research executions
 * @since 1.0.0
 */
const ResearchListResponse = Schema.Array(ResearchStatusResponse);

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Research RPC group
 *
 * Provides research execution APIs with streaming:
 * - Execute research (streaming)
 * - Get research status
 * - List user's research executions
 *
 * @since 1.0.0
 */
export class ResearchRpcs extends RpcGroup.make(
  Rpc.make("research.execute", {
    payload: ExecuteResearchRequest,
    success: ResearchEventSchema,
    error: Schema.Union(Unauthorized, DatabaseError, RedisStreamError),
    stream: true,
  }).middleware(AuthMiddleware),
  Rpc.make("research.status", {
    payload: ResearchStatusRequest,
    success: ResearchStatusResponse,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("research.list", {
    success: ResearchListResponse,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
) {}
